import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Home, ChevronRight, ChevronDown, Share2 } from 'lucide-react';
import ShareModal from '../components/ShareModal';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeSanitize from 'rehype-sanitize';
import markdownStyles from '../components/markdown-styles.module.css';
import { fetchChatHistory } from '../slice/api/historySlice';
import { fetchSQByTaskId } from '../slice/sqSlice';
import { fetchQAHistory } from '../slice/qaHistorySlice';
import { getTaskStatusesByHistoryAsync, updateAnswerStatusAsync } from '../slice/taskStatusSlice';
import { fetchUserFiles } from '../slice/apiSlice';
import {
  fetchWidgetData,
  generateWidget,
} from '../slice/projectWidgetsSlice';
import { fetchWidgetCatalog } from '../slice/agentWidgetsSlice';
import WidgetCard from '../components/sub-components/WidgetCard';
import { useThemeColor } from '../hooks/useThemeColor';
import { uploadFilesWithHistory } from '../Api/req';
import { toast } from 'react-hot-toast';
import { useTranslation } from '../i18n/LanguageContext';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

const ProjectDetail = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { aispaceColor } = useThemeColor();
  const { t, language } = useTranslation();

  // Get project name from navigation state (passed from History page)
  const projectNameFromState = location.state?.projectName;

  const userId = localStorage.getItem('id');
  const { chatHistory: chatHistoryResponse, status: historyStatus } = useSelector((state) => state.history);
  const chatHistory = chatHistoryResponse?.history_records;
  const loading = historyStatus === 'loading';
  const { questions, fetchLoading: questionsLoading } = useSelector((state) => state.sq);
  const taskStatuses = useSelector((state) => state.taskStatus.taskStatuses);
  const { qaHistory } = useSelector((state) => state.qaHistory);
  const { userFiles, userFilesStatus } = useSelector((state) => state.api);
  const { data: userData } = useSelector((state) => state.admin);
  const {
    historyId: widgetHistoryId,
    layout: widgetLayout,
    widgets: widgetData,
    derived: widgetDerived,
    generatingKey,
    isOverviewEnabled,
    showProjectDetail,
    status: widgetStatus,
  } = useSelector((state) => state.projectWidgets);
  const { catalog: widgetCatalog } = useSelector((state) => state.agentWidgets);

  const [activeTab, setActiveTab] = useState('overview');
  const [showShareModal, setShowShareModal] = useState(false);

  // Documents tab state
  const [docSearchQuery, setDocSearchQuery] = useState('');
  const [userDocsPage, setUserDocsPage] = useState(1);
  const [defaultDocsPage, setDefaultDocsPage] = useState(1);
  const docsPerPage = 3;

  // File upload state
  const fileInputRef = useRef(null);
  const [isUploading, setIsUploading] = useState(false);

  // Questions tab state
  const [questionsPage, setQuestionsPage] = useState(1);
  const questionsPerPage = 10;
  const [expandedQuestionId, setExpandedQuestionId] = useState(null);
  const [statusDropdownForId, setStatusDropdownForId] = useState(null);
  const [savingStatusForId, setSavingStatusForId] = useState(null);
  const statusDropdownRef = useRef(null);

  const ANSWER_STATUSES = [
    { key: 'accepted', color: '#10B981' },
    { key: 'review', color: '#F59E0B' },
    { key: 'missing', color: '#F97316' },
    { key: 'rejected', color: '#EF4444' },
  ];

  // Close status dropdown when clicking outside
  useEffect(() => {
    if (statusDropdownForId === null) return;
    const handleClickOutside = (event) => {
      if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target)) {
        setStatusDropdownForId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusDropdownForId]);

  const handleAnswerStatusChange = async (questionId, statusKey) => {
    setStatusDropdownForId(null);
    setSavingStatusForId(questionId);
    try {
      await dispatch(updateAnswerStatusAsync({
        history_id: projectId,
        sq_id: questionId,
        answer_status: statusKey,
      })).unwrap();
    } catch (err) {
      console.error('[AnswerStatus] save failed:', err);
    } finally {
      setSavingStatusForId(null);
    }
  };

  // Activities tab state
  const [showAllActivities, setShowAllActivities] = useState(false);
  const initialActivitiesCount = 5;

  // Filter and separate files
  const userFilesList = userFiles?.files?.filter(f => !f.is_default) || [];
  const defaultFilesList = userFiles?.files?.filter(f => f.is_default) || [];

  // Filtered user files based on search
  const filteredUserFiles = userFilesList.filter(file =>
    file.filename.toLowerCase().includes(docSearchQuery.toLowerCase())
  );

  // Reset pagination when search changes
  useEffect(() => {
    setUserDocsPage(1);
  }, [docSearchQuery]);

  // If admin has disabled the Project Overview tab, bounce the user to Documents.
  // Guard on widgetHistoryId === projectId so stale Redux state from a previously
  // visited project (which may have had overview disabled) never triggers this
  // redirect for a project where overview IS enabled.
  useEffect(() => {
    if (
      widgetStatus === 'succeeded' &&
      widgetHistoryId === projectId &&
      !isOverviewEnabled &&
      activeTab === 'overview'
    ) {
      setActiveTab('documents');
    }
  }, [isOverviewEnabled, activeTab, widgetStatus, widgetHistoryId, projectId]);

  // Fetch project data
  useEffect(() => {
    if (projectId && userId) {
      dispatch(fetchChatHistory({ historyId: projectId, userId }));
    }
  }, [dispatch, projectId, userId]);

  // Debug: Log chatHistory and Standard Questions data
  useEffect(() => {
    console.log('=== ProjectDetail Debug ===');
    console.log('chatHistory:', chatHistory);
    console.log('task_id:', chatHistory?.task_id);
    console.log('Standard Questions:', questions);
    console.log('questionsLoading:', questionsLoading);
    console.log('taskStatuses:', taskStatuses);
  }, [chatHistory, questions, questionsLoading, taskStatuses]);

  // Fetch Standard Questions, QA History and statuses when we have task_id
  // Skip SQ fetch when questions are disabled for this agent
  useEffect(() => {
    if (chatHistory?.task_id && projectId && userId) {
      if (showProjectDetail !== false) {
        dispatch(fetchSQByTaskId(chatHistory.task_id));
      }
      dispatch(getTaskStatusesByHistoryAsync(projectId));
      dispatch(fetchQAHistory({
        historyId: projectId,
        userId,
        taskId: chatHistory.task_id
      }));
    }
  }, [dispatch, chatHistory?.task_id, projectId, userId, showProjectDetail]);

  // Fetch user files - requires task_id for default files
  useEffect(() => {
    if (projectId && userId && chatHistory?.task_id) {
      dispatch(fetchUserFiles({
        userId,
        historyId: projectId,
        taskId: chatHistory.task_id,
        organizationId: userData?.organization_id || null
      }));
    }
  }, [dispatch, projectId, userId, chatHistory?.task_id, userData?.organization_id]);

  // Fetch persisted widget data (layout + values + derived counts)
  useEffect(() => {
    if (projectId) dispatch(fetchWidgetData(projectId));
  }, [dispatch, projectId]);

  // Ensure widget catalog is available for type metadata
  useEffect(() => {
    if (!widgetCatalog || widgetCatalog.length === 0) {
      dispatch(fetchWidgetCatalog());
    }
  }, [widgetCatalog, dispatch]);

  // Helper: trigger refresh for a given widget key
  const refreshWidget = (widgetKey) => {
    if (!projectId) return;
    dispatch(generateWidget({ historyId: projectId, widgetKey }));
  };

  // Auto-generate LLM widgets on first load if we have files but no data yet.
  // Uses the task's layout to decide which LLM widgets to kick off.
  const autoGenTriggered = useRef(false);
  useEffect(() => {
    if (!projectId || autoGenTriggered.current) return;
    if (!widgetLayout || widgetLayout.length === 0) return;
    if (!widgetCatalog || widgetCatalog.length === 0) return;
    const hasFiles = (userFiles?.files?.length || 0) > 0;
    if (!hasFiles) return;
    const hasAny = Object.keys(widgetData || {}).length > 0 &&
      Object.values(widgetData).some(v => v && (v.text || v.formattedAddress || v.date));
    if (hasAny) return;
    autoGenTriggered.current = true;
    const catalogByKey = widgetCatalog.reduce((m, c) => { m[c.key] = c; return m; }, {});
    widgetLayout
      .filter(l => l.is_enabled)
      .map(l => l.widget_key)
      .filter(k => catalogByKey[k]?.is_llm)
      .forEach((k) =>
        dispatch(generateWidget({ historyId: projectId, widgetKey: k }))
      );
  }, [projectId, userFiles, widgetData, widgetLayout, widgetCatalog, dispatch]);

  // Legacy progress vars kept for any reference elsewhere; WidgetCard owns the gauge now.
  // Scope to this project's history — `taskStatuses` is a global accumulating array.
  const completedQuestions = (taskStatuses || []).filter(
    s => s.history_id === projectId && s.status === true
  ).length;
  const totalQuestions = questions?.length || 0;

  // Helper function to get date label
  const getDateLabel = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return t('projectDetail.activityTodayLabel');
    } else if (date.toDateString() === yesterday.toDateString()) {
      return t('projectDetail.activityYesterdayLabel');
    } else {
      return date.toLocaleDateString(language, { day: 'numeric', month: 'long', year: 'numeric' });
    }
  };

  // Generate activities from files and task statuses
  const generateActivities = () => {
    const activities = [];

    // Add file upload activities
    const allFiles = userFiles?.files || [];
    allFiles.forEach((file) => {
      activities.push({
        id: `file-${file.id || file.filename}`,
        type: 'file_uploaded',
        title: t('projectDetail.activityFileUploaded'),
        description: file.filename,
        status: file.is_processed ? t('projectDetail.activityFileProcessed') : null,
        date: file.created_at || new Date().toISOString(),
        user: {
          name: userData?.name || localStorage.getItem('name') || t('projectDetail.activityDefaultUser'),
          avatar: userData?.avatar || null
        }
      });
    });

    // Add question asked and AI response activities from QA History
    qaHistory?.forEach((message) => {
      if (message.role === 'user') {
        // User asked a question
        activities.push({
          id: `question-${message.id}`,
          type: 'question_asked',
          title: t('projectDetail.activityQuestionAsked'),
          description: message.content?.substring(0, 80) + (message.content?.length > 80 ? '...' : '') || t('projectDetail.activityQuestionAskedFallback'),
          date: message.timestamp || new Date().toISOString(),
          user: {
            name: userData?.name || localStorage.getItem('name') || t('projectDetail.activityDefaultUser'),
            avatar: userData?.avatar || null
          }
        });
      } else if (message.role === 'assistant') {
        // AI performed analysis/response
        activities.push({
          id: `analysis-${message.id}`,
          type: 'analysis_performed',
          title: t('projectDetail.activityAnalysisPerformed'),
          description: message.content?.substring(0, 80) + (message.content?.length > 80 ? '...' : '') || t('projectDetail.activityAnalysisFallback'),
          date: message.timestamp || new Date().toISOString(),
          user: {
            name: t('projectDetail.activityAiAgent'),
            avatar: null,
            isAI: true
          }
        });
      }
    });

    // Add project creation activity
    if (chatHistory?.created_at) {
      activities.push({
        id: 'project-created',
        type: 'project_created',
        title: t('projectDetail.projectCreated'),
        description: chatHistory?.history_name || t('projectDetail.newProjectStarted'),
        date: chatHistory.created_at,
        user: {
          name: userData?.name || localStorage.getItem('name') || t('projectDetail.activityDefaultUser'),
          avatar: userData?.avatar || null
        }
      });
    }

    // Sort by date (newest first)
    activities.sort((a, b) => new Date(b.date) - new Date(a.date));

    return activities;
  };

  // Group activities by date
  const groupActivitiesByDate = (activities) => {
    const grouped = {};
    activities.forEach((activity) => {
      const label = getDateLabel(activity.date);
      if (!grouped[label]) {
        grouped[label] = [];
      }
      grouped[label].push(activity);
    });
    return grouped;
  };

  const allActivities = generateActivities();
  const displayedActivities = showAllActivities ? allActivities : allActivities.slice(0, initialActivitiesCount);
  const groupedActivities = groupActivitiesByDate(displayedActivities);

  // Get initials from name
  const getInitials = (name) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Get activity icon based on type
  const getActivityIcon = (type) => {
    switch (type) {
      case 'file_uploaded':
        return (
          <div className="w-2 h-2 rounded-full bg-[#212121] mt-2"></div>
        );
      case 'analysis_performed':
        return (
          <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center">
            <span className="text-[12px] font-medium text-[#6B7280]">A</span>
          </div>
        );
      case 'question_asked':
        return (
          <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center">
            <span className="text-[12px] font-medium text-[#6B7280]">Q</span>
          </div>
        );
      case 'project_created':
        return (
          <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 5v14"/>
              <path d="M5 12h14"/>
            </svg>
          </div>
        );
      default:
        return (
          <div className="w-6 h-6 rounded-full bg-[#F3F4F6] flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6B7280" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <circle cx="12" cy="12" r="4"/>
            </svg>
          </div>
        );
    }
  };

  // Days until submission — prefer extracted submission_date, else fallback placeholder
  const getDaysUntilDeadline = () => {
    const submission = widgetData?.submission_date;
    if (submission?.date) {
      const target = new Date(submission.date);
      if (!isNaN(target.getTime())) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const days = Math.ceil((target - today) / (1000 * 60 * 60 * 24));
        return {
          days,
          date: target.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }),
        };
      }
    }
    const deadline = new Date();
    deadline.setDate(deadline.getDate() + 17);
    return { days: 17, date: deadline.toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) };
  };

  const deadlineInfo = getDaysUntilDeadline();

  // Handle continue button
  const handleContinue = () => {
    navigate(`/analysis/${projectId}`);
  };

  // Handle file upload click
  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  // Handle file selection and upload
  const handleFileChange = async (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    if (selectedFiles.length === 0) return;

    // Validate files
    const validExtensions = ['.pdf', '.doc', '.docx', '.txt', '.xls', '.xlsx', '.ppt', '.pptx', '.jpg', '.jpeg', '.png'];
    const validFiles = [];
    let acceptedImageCount = 0;

    for (const file of selectedFiles) {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!validExtensions.includes(ext)) {
        toast.error(`Ungültiger Dateityp: ${file.name}`);
        continue;
      }

      // Count pages for PDFs
      if (file.type === 'application/pdf') {
        try {
          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          file._pageCount = pdf.numPages;
          await pdf.destroy();
        } catch (err) {
          toast.error(`Fehler beim Verarbeiten von ${file.name}`);
          continue;
        }
      } else if (file.type?.startsWith('image/')) {
        // Document Intelligence analyzes an image directly — no pages to
        // split/count like a PDF, so this is always exactly 1 for OCR quota
        // purposes (matches ChatInput.jsx/Sidebar.jsx's identical handling).
        if (acceptedImageCount >= 3) {
          toast.error(t('chatActions.tooManyImages', { max: 3 }));
          continue;
        }
        file._pageCount = 1;
        acceptedImageCount += 1;
      } else {
        file._pageCount = 0;
      }
      validFiles.push(file);
    }

    if (validFiles.length === 0) {
      fileInputRef.current.value = '';
      return;
    }

    // Upload files
    setIsUploading(true);
    try {
      const taskId = chatHistory?.task_id || '40';
      const page_counts = validFiles.map(f => f._pageCount || 0);

      await uploadFilesWithHistory(validFiles, userId, taskId, projectId, page_counts);

      toast.success('Dokumente erfolgreich hochgeladen');

      // Refresh file list
      dispatch(fetchUserFiles({
        userId,
        historyId: projectId,
        taskId: taskId,
        organizationId: userData?.organization_id
      }));

      // Auto-generate the LLM-backed widgets enabled in the task's layout
      const catalogByKey = (widgetCatalog || []).reduce(
        (m, c) => { m[c.key] = c; return m; }, {}
      );
      (widgetLayout || [])
        .filter(l => l.is_enabled)
        .map(l => l.widget_key)
        .filter(k => catalogByKey[k]?.is_llm)
        .forEach((k) =>
          dispatch(generateWidget({ historyId: projectId, widgetKey: k }))
        );
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(
        error?.code === 'upload_page_limit_exceeded'
          ? t('chatActions.uploadPageLimitExceeded', { limit: error.limit, attempted: error.attempted })
          : 'Fehler beim Hochladen der Dokumente'
      );
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-t-transparent" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
      </div>
    );
  }

  const tabs = [
    ...(isOverviewEnabled ? [{ id: 'overview', label: t('projectDetail.tabOverview') }] : []),
    { id: 'documents', label: t('projectDetail.tabDocuments') },
    ...(showProjectDetail !== false ? [{ id: 'questions', label: t('projectDetail.tabQuestions') }] : []),
    { id: 'activities', label: t('projectDetail.tabActivities') },
  ];

  // Overlay task-status counts onto derived data for the Checklist bar widget.
  // Two filters needed:
  //   1. Scope to THIS project — `taskStatuses` is a global redux array that
  //      accumulates rows across every history the user has opened, so without
  //      this filter another project's statuses leak into this project's bars
  //      and gauge.
  //   2. Drop orphan rows whose sq_id is no longer in the SQ catalog (deleted
  //      standard questions) so they don't inflate counts.
  const validSqIds = new Set((questions || []).map(q => parseInt(q.id)));
  const liveStatuses = (taskStatuses || []).filter(
    s => s.history_id === projectId && validSqIds.has(parseInt(s.sq_id))
  );
  const answerStatusCounts = liveStatuses.reduce(
    (acc, s) => {
      if (s.answer_status && acc.hasOwnProperty(s.answer_status)) {
        acc[s.answer_status] += 1;
      }
      return acc;
    },
    { accepted: 0, review: 0, missing: 0, rejected: 0 }
  );
  const totalQuestionsFallback = questions?.length || 0;
  // Single source of truth for both widgets: TASK_STATUS.answer_status, which
  // is what the UI actually writes. The backend's QuestionAnswerHistory.widget_status
  // path isn't wired from the FE in this branch, so we derive analysis_score
  // here too — otherwise the bars and the gauge tell different stories.
  const answeredTotal =
    answerStatusCounts.accepted +
    answerStatusCounts.review +
    answerStatusCounts.missing +
    answerStatusCounts.rejected;
  const derivedWithFallback = {
    ...widgetDerived,
    checklist: {
      ...(widgetDerived?.checklist || {}),
      accepted: answerStatusCounts.accepted,
      further_review: answerStatusCounts.review,
      info_missing: answerStatusCounts.missing,
      rejected: answerStatusCounts.rejected,
      pending: Math.max(0, totalQuestionsFallback - answeredTotal),
    },
    analysis_score: {
      accepted: answerStatusCounts.accepted,
      total: answeredTotal,
      percentage: answeredTotal
        ? Math.round((100 * answerStatusCounts.accepted) / answeredTotal)
        : 0,
    },
  };

  // Build the ordered list of widgets to render (layout is agent-scoped).
  const catalogByKey = (widgetCatalog || []).reduce((m, c) => { m[c.key] = c; return m; }, {});
  const enabledWidgets = (widgetLayout || [])
    .filter(l => l.is_enabled && catalogByKey[l.widget_key])
    .sort((a, b) => a.position - b.position);

  const lsName = localStorage.getItem('name');
  const cleanLsName = (lsName && lsName !== 'null' && lsName !== 'None' && lsName !== 'undefined') ? lsName : '';
  const currentUserForModal = {
    name: userData?.user_fullname || cleanLsName || '',
    email: userData?.user_email || localStorage.getItem('email') || '',
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] p-6">
      {/* Breadcrumb */}
      <div className="flex items-center gap-2 text-[14px] text-[#6B7280] mb-6">
        <Home size={16} />
        <span className="cursor-pointer hover:text-[#374151]" onClick={() => navigate('/')}>{t('nav.homepage')}</span>
        <ChevronRight size={16} />
        <span className="cursor-pointer hover:text-[#374151]" onClick={() => navigate('/history')}>{t('history.pageTitle')}</span>
        <ChevronRight size={16} />
        <span className="text-[#374151] font-medium">{t('projectDetail.breadcrumb')}</span>
      </div>

      {/* Header Card */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-[24px] font-semibold text-[#212121]">
              {projectNameFromState || chatHistory?.history_name || t('history.pageTitle')}
            </h1>
            <p className="text-[14px] text-[#6B7280] mt-1">
              {t('projectDetail.agentLabel', { name: chatHistory?.task_name || t('projectDetail.defaultAgentName') })}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowShareModal(true)}
              className="flex items-center gap-2 px-4 py-2.5 text-[14px] font-medium border rounded-lg transition-colors hover:bg-[#F9FAFB]"
              style={{ borderColor: aispaceColor, color: aispaceColor }}
            >
              <Share2 size={15} />
              {t('share.button')}
            </button>
            <button
              onClick={handleContinue}
              className="px-6 py-2.5 text-white rounded-lg text-[14px] font-medium hover:opacity-90 transition-colors"
              style={{ backgroundColor: aispaceColor }}
            >
              {t('history.continue')}
            </button>
          </div>
        </div>
      </div>

      {/* Tabs - Outside the card */}
      <div className="border-b border-[#E5E7EB] mb-6">
        <div className="flex items-center gap-6">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="pb-3 text-[14px] font-medium transition-colors relative"
              style={{ color: activeTab === tab.id ? aispaceColor : '#9E9E9E' }}
            >
              {tab.label}
              {activeTab === tab.id && (
                <div
                  className="absolute bottom-0 left-0 right-0 h-[2px]"
                  style={{ backgroundColor: aispaceColor }}
                />
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content Grid — layout driven by the agent's saved Project Overview config */}
      {activeTab === 'overview' && isOverviewEnabled && (
        <div className="grid grid-cols-3 gap-4">
          {enabledWidgets.length === 0 ? (
            <div className="col-span-3 text-center text-[14px] text-[#9CA3AF] py-12 border-2 border-dashed border-[#E5E7EB] rounded-xl">
              Keine Widgets konfiguriert. Der Agent-Administrator kann Widgets im
              <span className="font-medium"> Ai Request Overview</span>-Tab der Agenteneinstellungen hinzufügen.
            </div>
          ) : (
            enabledWidgets.map((entry) => {
              const item = catalogByKey[entry.widget_key];
              return (
                <WidgetCard
                  key={entry.widget_key}
                  catalogItem={item}
                  data={widgetData?.[entry.widget_key] || {}}
                  derived={derivedWithFallback}
                  aispaceColor={aispaceColor}
                  mode="live"
                  isGenerating={generatingKey === entry.widget_key}
                  onRefresh={() => refreshWidget(entry.widget_key)}
                />
              );
            })
          )}
        </div>
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div className="space-y-6">
          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileChange}
            accept=".pdf,.doc,.docx,.txt,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png"
            multiple
            style={{ display: 'none' }}
          />

          {/* Ihre Dokumente Section */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Header Row - Title and Upload Button */}
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-[18px] font-semibold text-[#212121]">{t('projectDetail.yourDocuments')}</h3>
              <button
                onClick={handleUploadClick}
                disabled={isUploading}
                className="flex items-center gap-2 px-4 py-2.5 text-white rounded-lg text-[14px] font-medium hover:opacity-90 transition-colors disabled:opacity-50"
                style={{ backgroundColor: aispaceColor }}
              >
                {isUploading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                )}
                {isUploading ? t('projectDetail.uploading') : t('projectDetail.uploadDocument')}
              </button>
            </div>

            {/* Search Input */}
            <div className="relative mb-6">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8"/>
                <line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder={t('common.search')}
                value={docSearchQuery}
                onChange={(e) => setDocSearchQuery(e.target.value)}
                className="w-full max-w-md pl-10 pr-4 py-2.5 border border-[#EEEEEE] rounded-lg text-[14px] focus:outline-none focus:ring-0"
                onFocus={(e) => e.target.style.borderColor = aispaceColor}
                onBlur={(e) => e.target.style.borderColor = '#EEEEEE'}
              />
            </div>

            {/* Table Header */}
            <div className="flex items-center px-4 py-3 bg-[#F5F5F5] rounded-t-lg border border-[#EEEEEE] border-b-0">
              <div className="flex-1 min-w-[200px] text-[13px] font-medium text-[#212121]">{t('projectDetail.documentName')}</div>
              <div className="w-[100px] flex-shrink-0 text-[13px] font-medium text-[#212121] text-center">{t('projectDetail.pages')}</div>
              <div className="w-[150px] flex-shrink-0 text-[13px] font-medium text-[#212121] text-right">{t('projectDetail.uploadDate')}</div>
            </div>

            {/* Table Body */}
            <div className="border border-[#EEEEEE] border-t-0 rounded-b-lg overflow-hidden">
              {userFilesStatus === 'loading' ? (
                <div className="py-8 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
                  <span className="ml-2 text-[14px] text-[#6B7280]">{t('projectDetail.documentsLoading')}</span>
                </div>
              ) : filteredUserFiles.length > 0 ? (
                filteredUserFiles.slice((userDocsPage - 1) * docsPerPage, userDocsPage * docsPerPage).map((file, index) => (
                  <div
                    key={index}
                    className={`flex items-center px-4 py-4 hover:bg-[#FAFAFA] ${index > 0 ? 'border-t border-[#EEEEEE]' : ''}`}
                  >
                    <div className="flex-1 min-w-[200px] text-[14px] text-[#616161]">{file.filename}</div>
                    <div className="w-[100px] flex-shrink-0 text-[14px] text-[#616161] text-center">{file.page_count || '-'}</div>
                    <div className="w-[150px] flex-shrink-0 text-[14px] text-[#616161] text-right">
                      {file.created_at ? new Date(file.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  {/* Decorative circle with document icon */}
                  <div className="relative mb-5">
                    <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                      {/* Decorative dots */}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      {/* Inner circle with document icon */}
                      <div className="w-14 h-14 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('projectDetail.noDocumentsUploaded')}</h3>
                  <p className="text-[13px] text-[#6B7280] text-center max-w-[300px]">Laden Sie Dateien in dieses Projekt hoch, damit der Agent sie überprüfen und analysieren kann.</p>
                </div>
              )}
            </div>

            {/* Pagination - only show when there are documents */}
            {filteredUserFiles.length > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#EEEEEE]">
                <span className="text-[14px] text-[#6B7280] font-medium">
                  {t('pagination.pageOf', { current: userDocsPage, total: Math.ceil(filteredUserFiles.length / docsPerPage) || 1 })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setUserDocsPage(prev => Math.max(1, prev - 1))}
                    disabled={userDocsPage === 1}
                    className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    onClick={() => setUserDocsPage(prev => Math.min(Math.ceil(filteredUserFiles.length / docsPerPage), prev + 1))}
                    disabled={userDocsPage >= Math.ceil(filteredUserFiles.length / docsPerPage)}
                    className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Agenten Referenzdateien Section */}
          <div className="bg-white rounded-xl shadow-sm p-6">
            {/* Section Title */}
            <h3 className="text-[18px] font-semibold text-[#212121] mb-6">{t('projectDetail.agentReferenceFiles')}</h3>

            {/* Table Header */}
            <div className="flex items-center px-4 py-3 bg-[#F5F5F5] rounded-t-lg border border-[#EEEEEE] border-b-0">
              <div className="flex-1 min-w-[200px] text-[13px] font-medium text-[#212121]">{t('projectDetail.documentName')}</div>
              <div className="w-[100px] flex-shrink-0 text-[13px] font-medium text-[#212121] text-center">{t('projectDetail.pages')}</div>
              <div className="w-[150px] flex-shrink-0 text-[13px] font-medium text-[#212121] text-right">{t('projectDetail.uploadDate')}</div>
            </div>

            {/* Table Body */}
            <div className="border border-[#EEEEEE] border-t-0 rounded-b-lg overflow-hidden">
              {userFilesStatus === 'loading' ? (
                <div className="py-8 flex items-center justify-center">
                  <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
                  <span className="ml-2 text-[14px] text-[#6B7280]">{t('projectDetail.documentsLoading')}</span>
                </div>
              ) : defaultFilesList.length > 0 ? (
                defaultFilesList.slice((defaultDocsPage - 1) * docsPerPage, defaultDocsPage * docsPerPage).map((file, index) => (
                  <div
                    key={index}
                    className={`flex items-center px-4 py-4 hover:bg-[#FAFAFA] ${index > 0 ? 'border-t border-[#EEEEEE]' : ''}`}
                  >
                    <div className="flex-1 min-w-[200px] text-[14px] text-[#616161]">{file.filename}</div>
                    <div className="w-[100px] flex-shrink-0 text-[14px] text-[#616161] text-center">{file.page_count || '-'}</div>
                    <div className="w-[150px] flex-shrink-0 text-[14px] text-[#616161] text-right">
                      {file.created_at ? new Date(file.created_at).toLocaleDateString('de-DE', { day: 'numeric', month: 'long', year: 'numeric' }) : '-'}
                    </div>
                  </div>
                ))
              ) : (
                <div className="flex flex-col items-center justify-center py-12">
                  {/* Decorative circle with document icon */}
                  <div className="relative mb-5">
                    <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                      {/* Decorative dots */}
                      <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                      {/* Inner circle with document icon */}
                      <div className="w-14 h-14 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                          <polyline points="14 2 14 8 20 8"/>
                        </svg>
                      </div>
                    </div>
                  </div>
                  <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">Keine Referenzdateien verfügbar</h3>
                  <p className="text-[13px] text-[#6B7280] text-center max-w-[320px]">Referenzdateien helfen dem Agenten, den Kontext zu verstehen und genauer zu antworten.</p>
                </div>
              )}
            </div>

            {/* Pagination - only show when there are reference files */}
            {defaultFilesList.length > 0 && (
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-[#EEEEEE]">
                <span className="text-[14px] text-[#6B7280] font-medium">
                  {t('pagination.pageOf', { current: defaultDocsPage, total: Math.ceil(defaultFilesList.length / docsPerPage) || 1 })}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setDefaultDocsPage(prev => Math.max(1, prev - 1))}
                    disabled={defaultDocsPage === 1}
                    className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.previous')}
                  </button>
                  <button
                    onClick={() => setDefaultDocsPage(prev => Math.min(Math.ceil(defaultFilesList.length / docsPerPage), prev + 1))}
                    disabled={defaultDocsPage >= Math.ceil(defaultFilesList.length / docsPerPage)}
                    className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {t('common.next')}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Questions Tab */}
      {activeTab === 'questions' && (
        <div className="border border-[#EEEEEE] rounded-lg overflow-hidden bg-white overflow-x-auto">
          <div className="min-w-[780px]">
          {/* Header Row */}
          <div className="flex items-center px-6 py-3 bg-[#F5F5F5]">
            <div className="flex-1 min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsCategory')}</div>
            <div className="flex-[1.5] min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsTitle')}</div>
            <div className="flex-[1.5] min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsDescription')}</div>
            <div className="flex-[2.5] min-w-0 text-[13px] font-medium text-[#212121] pr-4">{t('projectDetail.questionsAnswer')}</div>
            <div className="w-[210px] flex-shrink-0 text-[13px] font-medium text-[#212121] text-center">{t('projectDetail.questionsStatus')}</div>
          </div>

          {/* Body Rows */}
          <div>
            {questionsLoading ? (
              <div className="py-8 flex items-center justify-center border-t border-[#EEEEEE]">
                <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: aispaceColor, borderTopColor: 'transparent' }}></div>
                <span className="ml-2 text-[14px] text-[#6B7280]">{t('projectDetail.questionsLoading')}</span>
              </div>
            ) : questions?.length > 0 ? (() => {
              // Build a SQ-title -> latest AI answer map. The chat flow stores
              // each bot reply in QuestionAnswerHistory with `title` set to the
              // matching standard-question title; that's how Analysis.jsx links
              // answers to SQs (see titleToSqId there).
              const titleToAnswer = {};
              (qaHistory || []).forEach((msg) => {
                if (msg?.role !== 'user' && msg?.title && msg?.content) {
                  const key = String(msg.title).toLowerCase().trim();
                  titleToAnswer[key] = msg.content;
                }
              });
              return questions.slice((questionsPage - 1) * questionsPerPage, questionsPage * questionsPerPage).map((question, index) => {
                // Find status for this question from taskStatuses. Rows are keyed by sq_id,
                // and the global redux array carries rows for every history the user has
                // opened — so we must scope by history_id too, otherwise a status from
                // another project for the same SQ would bleed in.
                const questionStatus = (taskStatuses || []).find(
                  s => s.history_id === projectId && parseInt(s.sq_id) === parseInt(question.id)
                );
                const aiAnswer = question.title
                  ? titleToAnswer[String(question.title).toLowerCase().trim()]
                  : null;
                const currentAnswerStatus = questionStatus?.answer_status || null;
                const currentStatusMeta = ANSWER_STATUSES.find(s => s.key === currentAnswerStatus);
                const isExpanded = expandedQuestionId === question.id;
                const isSavingStatus = savingStatusForId === question.id;
                const isDropdownOpen = statusDropdownForId === question.id;

                return (
                  <div key={question.id || index} className="border-t border-[#EEEEEE]">
                    {/* Summary row — click to expand. Top-aligned so the first
                        line of every cell shares the same baseline regardless
                        of how many lines other cells wrap to; min-h pins the
                        row height so rows with short content match the visual
                        rhythm of rows with 2-line content. Column flex
                        weights match the header row above. */}
                    <div
                      className="flex items-start px-6 py-5 cursor-pointer hover:bg-[#FAFAFA] min-h-[88px]"
                      onClick={() => setExpandedQuestionId(isExpanded ? null : question.id)}
                    >
                      <div className="flex-1 min-w-0 text-[14px] text-[#616161] pr-4">
                        <span className="line-clamp-2">{question.category || t('projectDetail.defaultCategory')}</span>
                      </div>
                      <div className="flex-[1.5] min-w-0 text-[14px] text-[#616161] pr-4">
                        <span className="line-clamp-2">{question.title || '-'}</span>
                      </div>
                      <div className="flex-[1.5] min-w-0 text-[14px] text-[#616161] pr-4">
                        <span className="line-clamp-2">{question.description || '-'}</span>
                      </div>
                      <div className="flex-[2.5] min-w-0 text-[14px] text-[#616161] pr-4">
                        {aiAnswer ? (
                          // Inline markdown preview: render formatting (bold, italic, code,
                          // links) but force every block-level node to a span so the 2-line
                          // clamp still works. Skip the .reactMarkDown class so the 16px
                          // child rule from markdown-styles doesn't blow out the row height.
                          <div className="line-clamp-2 [&_p]:inline [&_strong]:font-semibold [&_em]:italic">
                            <ReactMarkdown
                              remarkPlugins={[remarkGfm]}
                              rehypePlugins={[rehypeSanitize]}
                              components={{
                                p: ({ node, ...props }) => <span {...props} />,
                                h1: ({ node, ...props }) => <span {...props} />,
                                h2: ({ node, ...props }) => <span {...props} />,
                                h3: ({ node, ...props }) => <span {...props} />,
                                h4: ({ node, ...props }) => <span {...props} />,
                                h5: ({ node, ...props }) => <span {...props} />,
                                h6: ({ node, ...props }) => <span {...props} />,
                                ul: ({ node, ...props }) => <span {...props} />,
                                ol: ({ node, ...props }) => <span {...props} />,
                                li: ({ node, children }) => <span>{children} </span>,
                                br: () => <span> </span>,
                                hr: () => <span> </span>,
                                blockquote: ({ node, ...props }) => <span {...props} />,
                                pre: ({ node, ...props }) => <span {...props} />,
                                code: ({ node, ...props }) => <code className="text-[13px]" {...props} />,
                                a: ({ node, ...props }) => <span className="underline" {...props} />,
                                table: () => null,
                              }}
                            >
                              {aiAnswer}
                            </ReactMarkdown>
                          </div>
                        ) : (
                          <span className="line-clamp-2">-</span>
                        )}
                      </div>
                      <div className="w-[210px] flex-shrink-0 flex items-center justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                        <div className="relative" ref={isDropdownOpen ? statusDropdownRef : null}>
                          <button
                            type="button"
                            onClick={() => !isSavingStatus && setStatusDropdownForId(isDropdownOpen ? null : question.id)}
                            disabled={isSavingStatus}
                            // Width is pinned via inline `width` so every pill
                            // is identical regardless of label length. Setting
                            // it through Tailwind's `w-[170px]` here was
                            // unreliable because the JIT scanner sometimes
                            // misses arbitrary values inside conditional
                            // template literals — inline style guarantees it.
                            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[12px] font-medium border transition-colors ${isSavingStatus ? 'opacity-70 cursor-wait' : 'hover:opacity-90'}`}
                            style={{
                              width: 170,
                              minWidth: 170,
                              maxWidth: 170,
                              boxSizing: 'border-box',
                              ...(currentStatusMeta ? {
                                backgroundColor: currentStatusMeta.color,
                                color: '#ffffff',
                                borderColor: currentStatusMeta.color,
                              } : {
                                backgroundColor: '#ffffff',
                                color: '#6B7280',
                                borderColor: '#E5E7EB',
                              }),
                            }}
                          >
                            {isSavingStatus ? (
                              <svg className="animate-spin h-3.5 w-3.5 flex-shrink-0" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                              </svg>
                            ) : !currentStatusMeta && (
                              <span className="inline-block w-2 h-2 rounded-full flex-shrink-0" style={{ backgroundColor: '#D1D5DB' }} />
                            )}
                            <span className="flex-1 min-w-0 truncate text-center">
                              {currentStatusMeta ? t(`answerStatus.${currentStatusMeta.key}`) : t('projectDetail.openAction')}
                            </span>
                            <ChevronDown size={12} className="flex-shrink-0" />
                          </button>
                          {isDropdownOpen && (
                            <div className="absolute right-0 top-full mt-1 w-56 bg-white rounded-lg shadow-lg border border-[#E5E7EB] z-50 py-1">
                              {ANSWER_STATUSES.map((s) => (
                                <button
                                  key={s.key}
                                  type="button"
                                  onClick={() => handleAnswerStatusChange(question.id, s.key)}
                                  className="w-full px-4 py-2 text-left hover:bg-[#F9FAFB] flex items-center gap-3"
                                >
                                  <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
                                  <span className="text-[13px] font-medium text-[#374151] truncate">{t(`answerStatus.${s.key}`)}</span>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => setExpandedQuestionId(isExpanded ? null : question.id)}
                          className="p-1 rounded text-[#9CA3AF] hover:text-[#374151] hover:bg-[#F3F4F6]"
                          title={isExpanded ? t('common.previous') : t('common.next')}
                        >
                          <ChevronDown size={16} className={`transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded detail panel — scrollable, markdown-rendered */}
                    {isExpanded && (
                      <div className="px-6 pb-6 bg-[#FAFAFA]">
                        <div className="max-h-[480px] overflow-y-auto thin-scrollbar pr-2 space-y-4">
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsCategory')}</div>
                            <div className="text-[14px] text-[#374151]">{question.category || t('projectDetail.defaultCategory')}</div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsTitle')}</div>
                            <div className="text-[14px] text-[#212121] font-medium">{question.title || '-'}</div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsDescription')}</div>
                            <div className="text-[14px] text-[#374151] whitespace-pre-wrap">{question.description || '-'}</div>
                          </div>
                          <div>
                            <div className="text-[11px] uppercase tracking-wide text-[#9CA3AF] font-semibold mb-1">{t('projectDetail.questionsAnswer')}</div>
                            {aiAnswer ? (
                              // `.reactMarkDown>*` in markdown-styles.module.css forces
                              // 16px on every direct child, which overrides our
                              // text-[14px] wrapper. Pass per-element overrides so
                              // the markdown body matches the 14px sizing of the
                              // Category / Title / Description values above.
                              <div className={`text-[14px] text-[#374151] ${markdownStyles.reactMarkDown}`}>
                                <ReactMarkdown
                                  remarkPlugins={[remarkGfm]}
                                  rehypePlugins={[rehypeSanitize]}
                                  components={{
                                    p: ({ node, ...props }) => {
                                      const isInListItem = node?.parent && node.parent.tagName === 'li';
                                      return <p style={isInListItem ? { fontWeight: 'normal', fontSize: '14px' } : { fontSize: '14px' }} {...props} />;
                                    },
                                    li: ({ node, ...props }) => <li style={{ fontWeight: 'normal', fontSize: '14px' }} {...props} />,
                                    table: ({ node, ...props }) => <table className="border-collapse border border-gray-300 my-3 text-[14px]" {...props} />,
                                    thead: ({ node, ...props }) => <thead className="bg-gray-100" {...props} />,
                                    tr: ({ node, ...props }) => <tr className="border-b border-gray-300" {...props} />,
                                    th: ({ node, ...props }) => <th className="border border-gray-300 px-3 py-1.5 text-left font-semibold text-[14px]" {...props} />,
                                    td: ({ node, ...props }) => <td className="border border-gray-300 px-3 py-1.5 text-[14px]" {...props} />,
                                  }}
                                >
                                  {aiAnswer}
                                </ReactMarkdown>
                              </div>
                            ) : (
                              <div className="text-[14px] italic text-[#9CA3AF]">-</div>
                            )}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              });
            })() : !chatHistory?.task_id ? (
              <div className="py-8 text-center text-[14px] text-[#6B7280] border-t border-[#EEEEEE]">
                {t('history.projectLoading')}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-12 border-t border-[#EEEEEE]">
                {/* Decorative circle with question mark icon */}
                <div className="relative mb-5">
                  <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                    {/* Decorative dots */}
                    <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                    {/* Inner circle with question mark icon */}
                    <div className="w-14 h-14 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"/>
                        <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                        <line x1="12" y1="17" x2="12.01" y2="17"/>
                      </svg>
                    </div>
                  </div>
                </div>
                <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('projectDetail.noQuestionsAssigned')}</h3>
                <p className="text-[13px] text-[#6B7280] text-center max-w-[300px]">{t('projectDetail.noQuestionsHint')}</p>
              </div>
            )}
          </div>

          {/* Pagination - only show when there are questions */}
          {questions?.length > 0 && (
            <div className="flex items-center justify-between px-6 py-3 border-t border-[#EEEEEE]">
              <span className="text-[14px] text-[#6B7280] font-medium">
                {t('pagination.pageOf', { current: questionsPage, total: Math.ceil((questions?.length || 0) / questionsPerPage) || 1 })}
              </span>
              <div className="flex gap-2">
                <button
                  onClick={() => setQuestionsPage(prev => Math.max(1, prev - 1))}
                  disabled={questionsPage === 1}
                  className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.previous')}
                </button>
                <button
                  onClick={() => setQuestionsPage(prev => Math.min(Math.ceil((questions?.length || 0) / questionsPerPage), prev + 1))}
                  disabled={questionsPage >= Math.ceil((questions?.length || 0) / questionsPerPage)}
                  className="px-4 py-1.5 text-[14px] font-semibold text-[#616161] border border-[#EEEEEE] rounded-md hover:bg-[#F5F5F5] disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {t('common.next')}
                </button>
              </div>
            </div>
          )}
          </div>
        </div>
      )}

      {/* Activities Tab */}
      {activeTab === 'activities' && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-[#E5E7EB]">
          {allActivities.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16">
              {/* Decorative circle with activity/pulse icon */}
              <div className="relative mb-5">
                <div className="w-[100px] h-[100px] rounded-full border-2 border-dashed border-[#E5E7EB] flex items-center justify-center relative">
                  {/* Decorative dots */}
                  <div className="absolute -top-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute top-1/2 -left-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  <div className="absolute top-1/2 -right-1 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-[#E5E7EB]"></div>
                  {/* Inner circle with pulse/activity icon */}
                  <div className="w-14 h-14 rounded-full bg-[#F9FAFB] border border-[#E5E7EB] flex items-center justify-center">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#9CA3AF" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                  </div>
                </div>
              </div>
              <h3 className="text-[15px] font-semibold text-[#111827] mb-1.5">{t('projectDetail.activitiesEmptyTitle')}</h3>
              <p className="text-[13px] text-[#6B7280] text-center max-w-[340px]">{t('projectDetail.activitiesEmpty')}</p>
            </div>
          ) : (
            <>
              {Object.entries(groupedActivities).map(([dateLabel, activities]) => (
                <div key={dateLabel} className="mb-8 last:mb-0">
                  <p className="text-[12px] font-semibold text-[#6B7280] mb-4">{dateLabel}</p>

                  {activities.map((activity, index) => (
                    <div key={activity.id} className="flex gap-3">
                      {/* Timeline with icon and connecting line */}
                      <div className="flex flex-col items-center">
                        {getActivityIcon(activity.type)}
                        {/* Vertical line connecting to next item */}
                        {index < activities.length - 1 && (
                          <div className="w-[1px] bg-[#E5E7EB] flex-1 min-h-[60px] mt-2"></div>
                        )}
                      </div>
                      <div className={`flex-1 ${index < activities.length - 1 ? 'pb-6' : ''}`}>
                        <p className="text-[16px] font-semibold text-[#212121]">{activity.title}</p>
                        <p className="text-[14px] text-[#616161] mt-1">
                          {activity.description}
                          {activity.status && ` — ${activity.status}`}
                        </p>
                        {activity.user && (
                          <div className="flex items-center gap-2 mt-2">
                            {activity.user.avatar ? (
                              <img
                                src={activity.user.avatar}
                                alt={activity.user.name}
                                className="w-6 h-6 rounded-full object-cover"
                              />
                            ) : (
                              <div
                                className="w-6 h-6 rounded-full flex items-center justify-center"
                                style={{ backgroundColor: activity.user.isAI ? aispaceColor : '#F3F4F6' }}
                              >
                                <span className={`text-[10px] font-semibold ${activity.user.isAI ? 'text-white' : 'text-[#6B7280]'}`}>
                                  {getInitials(activity.user.name)}
                                </span>
                              </div>
                            )}
                            <span className="text-[14px] font-semibold text-[#212121]">{activity.user.name}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ))}

              {/* Show older button */}
              {allActivities.length > initialActivitiesCount && (
                <button
                  onClick={() => setShowAllActivities(!showAllActivities)}
                  className="flex items-center gap-1 text-[14px] font-medium hover:opacity-80 transition-opacity"
                  style={{ color: aispaceColor }}
                >
                  <svg
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={`transition-transform ${showAllActivities ? 'rotate-180' : ''}`}
                  >
                    <polyline points="6 9 12 15 18 9"/>
                  </svg>
                  {showAllActivities ? t('projectDetail.activityShowLess') : t('projectDetail.activityShowOlder')}
                </button>
              )}
            </>
          )}
        </div>
      )}
      <ShareModal
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
        historyId={projectId}
        projectName={projectNameFromState || chatHistory?.history_name || ''}
        currentUser={currentUserForModal}
      />
    </div>
  );
};

export default ProjectDetail;
